import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";
import Layout from "@/components/Layout";
import ServiceCard from "@/components/ServiceCard";
import { useLanguage } from "@/contexts/LanguageContext";
import heroImage from "@/assets/hero-elevator.jpg";
import maintenanceImage from "@/assets/maintenance.jpg";
import accessibilityImage from "@/assets/accessibility.jpg";
import freightImage from "@/assets/freight.jpg";
import modernizationImage from "@/assets/modernization.jpg";

const Services = () => {
  const { language, t } = useLanguage();

  const services =
    language === "en"
      ? [
          {
            title: "Elevator Design & Installation",
            description:
              "We cover the full range of hydraulic or traction elevators for passengers or freight, with or without machine room, from 0.30 m/s up to 8.50 m/s.",
            image: modernizationImage,
            href: "/services/installation",
          },
          {
            title: "Elevator Maintenance & Repair",
            description:
              "Regular maintenance is essential for safe operation. We undertake maintenance and repairs with consistency and professionalism.",
            image: maintenanceImage,
            href: "/services/maintenance",
          },
          {
            title: "Elevator Modernization",
            description:
              "We propose the best upgrade solutions—mechanical improvements, safety components, and aesthetic upgrades for cabin and fixtures.",
            image: modernizationImage,
            href: "/services/modernization",
          },
          {
            title: "Accessibility Lifting Systems",
            description:
              "We install accessibility lifting systems indoors or outdoors, depending on needs and building constraints.",
            image: accessibilityImage,
            href: "/services/accessibility",
          },
          {
            title: "Freight Elevators",
            description:
              "Freight elevator installations for warehouses, shopping centers, industrial units and other professional spaces.",
            image: freightImage,
            href: "/services/freight",
          },
        ]
      : [
          {
            title: "Μελέτη & Εγκατάσταση Ανελκυστήρων",
            description:
              "Καλύπτουμε όλη την κλίμακα των υδραυλικών ή μηχανοκίνητων ανελκυστήρων, επιβατών ή φορτίων, με μηχανοστάσιο ή χωρίς, με ταχύτητες από 0,30 m/s έως 8,50 m/s.",
            image: modernizationImage,
            href: "/services/installation",
          },
          {
            title: "Συντήρηση & Επισκευή Ανελκυστήρων",
            description:
              "Η συντήρηση κάθε ανελκυστήρα αποτελεί απαραίτητη προϋπόθεση για την ομαλή και ασφαλή λειτουργία του. Αναλαμβάνουμε με συνέπεια και επαγγελματισμό.",
            image: maintenanceImage,
            href: "/services/maintenance",
          },
          {
            title: "Εκσυγχρονισμός Ανελκυστήρων",
            description:
              "Προτείνουμε τις καλύτερες λύσεις αναβάθμισης του ανελκυστήρα σας είτε αφορά βελτιώσεις μηχανικών μερών είτε αισθητικές βελτιώσεις.",
            image: modernizationImage,
            href: "/services/modernization",
          },
          {
            title: "Συστήματα Ανύψωσης ΑΜΕΑ",
            description:
              "Αναλαμβάνουμε την τοποθέτηση συστημάτων ανύψωσης ΑΜΕΑ σε εσωτερικό ή εξωτερικό χώρο, ανάλογα με τις ανάγκες.",
            image: accessibilityImage,
            href: "/services/accessibility",
          },
          {
            title: "Ανελκυστήρες Φορτίων",
            description:
              "Εγκατάσταση ανελκυστήρων φορτίων και ανύψωσης εμπορευμάτων σε αποθήκες, εμπορικά κέντρα, βιομηχανικές μονάδες.",
            image: freightImage,
            href: "/services/freight",
          },
        ];

  const hero =
    language === "en"
      ? {
          title: "Our Services",
          subtitle: "Complete elevator solutions with professionalism and reliability.",
          alt: "LIFTCO services",
          ctaTitle: t("common.needHelp"),
          ctaText: "Contact us for a free assessment and quote. We are available 24/7.",
        }
      : {
          title: "Οι Υπηρεσίες Μας",
          subtitle: "Πλήρεις λύσεις για ανελκυστήρες με επαγγελματισμό και αξιοπιστία.",
          alt: "LIFTCO υπηρεσίες",
          ctaTitle: t("common.needHelp"),
          ctaText: "Επικοινωνήστε μαζί μας για δωρεάν εκτίμηση και προσφορά. Είμαστε στη διάθεσή σας 24/7.",
        };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0">
          <img src={heroImage} alt={hero.alt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-hero-gradient" />
        </div>
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {hero.title}
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/80">{hero.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <ServiceCard key={service.title} {...service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {hero.ctaTitle}
            </h2>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto mb-8">{hero.ctaText}</p>
            <Button asChild variant="hero" size="lg">
              <a href="tel:2310270290">
                <Phone className="h-5 w-5" />
                2310 270290
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
